import getConfig from 'next/config';
import { trackError } from 'lib/error.utils';
import useApolloClient from '@honeybook/finance-components/dist/hooks/useApolloClient';
import { useCallback } from 'react';
import { ErrorHandler } from '@apollo/client/link/error';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { logger } from '@finance/shared/dist/utils/logger';
import appConfig from './config/appConfig';
import { AuthPages } from './lib/types/types';
import { isUserIdle } from './lib/client/monitorActivity';
import { doSignout } from './lib/client/signoutUtils';
import { useRouter } from 'next/router';

export default function useApollo(): ApolloClient<NormalizedCacheObject> {
    const { publicRuntimeConfig } = getConfig();
    const router = useRouter();

    const onError: ErrorHandler = useCallback(
        ({ graphQLErrors, networkError }) => {
            if (!graphQLErrors) {
                return;
            }
            graphQLErrors.forEach(error => {
                const {
                    message,
                    locations,
                    path,
                    extensions: { code, err }
                } = error;
                trackError({
                    error,
                    message: `GraphQL error`,
                    context: {
                        message,
                        locations,
                        path,
                        code
                    }
                });
                if (code === 'UNAUTHENTICATED') {
                    if (err == 'TokenExpiredError') {
                        if (isUserIdle()) {
                            doSignout();
                        } else {
                            router.push(AuthPages.signIn);
                        }
                    } else {
                        doSignout();
                    }
                }
            });

            if (networkError) logger.error(`[Network error]: ${networkError}`);
        },
        [router]
    );

    // Since we're passing _initApollo: true, we know this will never return null
    return useApolloClient({
        _initApollo: true,
        url: `${publicRuntimeConfig.serverBaseUrl}/api/graphql`,
        apiPath: '/api/graphql',
        batch: appConfig.useApolloBatch,
        errorCb: onError
    }) as ApolloClient<NormalizedCacheObject>;
}
